import { Stack, Text, ThemeIcon } from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconAlertTriangle,
  IconTrash,
  IconCheck,
  IconInfoCircle,
} from "@tabler/icons-react";
import type { FC, ReactNode } from "react";

export type ConfirmModalType = "danger" | "warning" | "info" | "success";

interface TypeConfigEntry {
  icon: FC<{ size?: number }>;
  color: string;
}

const typeConfig: Record<ConfirmModalType, TypeConfigEntry> = {
  danger: { icon: IconTrash, color: "rose" },
  warning: { icon: IconAlertTriangle, color: "amber" },
  info: { icon: IconInfoCircle, color: "cyan" },
  success: { icon: IconCheck, color: "green" },
};

export interface ConfirmOptions {
  title?: string;
  message?: string | ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  type?: ConfirmModalType;
  icon?: ReactNode;
}

export const useConfirmModal = () => {
  const confirm = ({
    title = "Confirm Action",
    message = "Are you sure you want to proceed?",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    type = "danger",
    icon,
  }: ConfirmOptions): Promise<boolean> => {
    const config = typeConfig[type];
    const IconComponent = config.icon;

    return new Promise<boolean>((resolve) => {
      modals.openConfirmModal({
        title,
        centered: true,
        withCloseButton: false,
        size: "sm",
        radius: "lg",
        padding: "lg",
        children: (
          <Stack gap="md">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: "var(--mantine-spacing-md)",
              }}
            >
              <ThemeIcon
                size={40}
                radius="xl"
                variant="light"
                color={config.color}
              >
                {icon ?? <IconComponent size={20} />}
              </ThemeIcon>
            </div>
            {typeof message === "string" ? (
              <Text ta="center" c="dark.2" style={{ lineHeight: 1.6 }}>
                {message}
              </Text>
            ) : (
              message
            )}
          </Stack>
        ),
        labels: { confirm: confirmLabel, cancel: cancelLabel },
        confirmProps: { color: config.color },
        groupProps: { justify: "center" },
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
        onClose: () => resolve(false),
      });
    });
  };

  return { confirm, modal: null };
};
